import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { formatInTimeZone } from "date-fns-tz";
import { ptBR } from "date-fns/locale";
import { jsonEvent } from "../../components/Json.js";
import {
  safeJsonParse,
  fillMissingProperties,
  convertDateToDisplayA,
  parseFlexibleDate,
} from "../../assets/functions.js";
import { statusX, colorsX } from "../../components/EventsComponents.js";
import { normalizeHomeRowLimits, normalizeLegacyHomeStreamings } from "../../utils/homeConfig.js";

export const initialState = {
  value: false,
  status: "idle",
  setTitleEvent: "",
  setDatetimeEvent: 0,
  setDatetimeOrder: 0,
  limit: 15,
  firstFetch: true,
  currentPage: 1,
  lastPage: 1,
  stopFetching: false,
  pageFetching: false,
  ownerUserId: null,
  setID: undefined,
  selectedEventPlatform: undefined,
  showHistory: false,
  showRecords: false,
  showEmails: false,
  showWhatsApp: false,
  showRsvps: false,
  showLogs: false,
  updateID: 0,
  platform: 0,
  allUsers: false,
  eventsOpened: [],
  eventsOpenedFull: [],
  draftEvents: [],
  eventGroup01Simple: [],
  eventGroup02Simple: [],
  eventGroup01: [],
  eventGroup02: [],
  usersList: [],
  setStatusEvent: [
    { value: 0, text: statusX[0], isActive: false },
    { value: 2, text: statusX[2], isActive: false },
    { value: 3, text: statusX[3], isActive: false },
    { value: 4, text: statusX[4], isActive: false },
  ],
};

// Shared helper — resets pagination fields back to their initial values.
// Called by any reducer that changes a filter and must restart from page 1.
const resetPagination = (state) => {
  state.currentPage = initialState.currentPage;
  state.lastPage = initialState.lastPage;
  state.pageFetching = initialState.pageFetching;
  state.stopFetching = initialState.stopFetching;
  state.firstFetch = initialState.firstFetch;
};

const clearSelectedEventState = (state) => {
  const selectedEventId = state.setID;
  const selectedPlatform = state.selectedEventPlatform ?? state.platform;

  state.eventsOpened = state.eventsOpened.filter(
    (event) =>
      !(
        String(event.evento_id) === String(selectedEventId) &&
        Number(event.platform) === Number(selectedPlatform)
      ),
  );
  state.eventsOpenedFull = initialState.eventsOpenedFull;
  state.setID = initialState.setID;
  state.selectedEventPlatform = initialState.selectedEventPlatform;
  state.showHistory = initialState.showHistory;
  state.showRecords = initialState.showRecords;
  state.showEmails = initialState.showEmails;
  state.showWhatsApp = initialState.showWhatsApp;
  state.showRsvps = initialState.showRsvps;
  state.showLogs = initialState.showLogs;
  state.value = initialState.value;
  resetPagination(state);
};

const dataTreatment = (value, index, type) => {
  if (value.aplicativos) {
    value.aplicativos.forEach((element) => {
      element.json = safeJsonParse(element.json);
    });
  }

  value["json"] = safeJsonParse(value.json);
  if (typeof value["app_details"] === "string") {
    try {
      value["app_details"] = JSON.parse(value["app_details"]);
    } catch (e) {
      console.error("Failed to parse JSON:", e);
      value["app_details"] = [];
    }
  }
  if (Array.isArray(value["app_details"]) && value["app_details"].length > 0) {
    value["app_details"] = Object.values(
      value["app_details"].reduce((acc, item) => {
        if (!acc[item.aplicativo_id]) {
          acc[item.aplicativo_id] = {
            aplicativo_id: item.aplicativo_id,
            nomeAplicativo: item.nomeAplicativo,
            titulo: item.titulo,
            customizacao_id: item.customizacao_id,
            imagem: item.imagem,
            version: item.version,
            currentVersion: item.currentVersion,
          };
        }
        return acc;
      }, {}),
    );
  }
  value["data_c"] = convertDateToDisplayA(value["data_criacao"], 1);
  value["data_t"] = convertDateToDisplayA(value["data_termino"], 1);
  const creationDate = parseFlexibleDate(value["data_criacao"]);
  const endDate = parseFlexibleDate(value["data_termino"]);
  value["data_criacao_formatted"] =
    !isNaN(creationDate.getTime())
      ? formatInTimeZone(creationDate, "UTC", "d 'de' MMMM", {
        locale: ptBR,
      })
      : "";
  value["data_termino_formatted"] =
    !isNaN(endDate.getTime())
      ? formatInTimeZone(endDate, "UTC", "d 'de' MMMM", {
        locale: ptBR,
      })
      : "";
  value["data_criacao"] = convertDateToDisplayA(value["data_criacao"], 2);
  value["data_termino"] = convertDateToDisplayA(value["data_termino"], 2);
  value["creation_date"] = convertDateToDisplayA(value["creation_date"], 2);
  value["end_date"] = convertDateToDisplayA(value["end_date"], 2);
  value["json"]["form"]["register_start_date"] = convertDateToDisplayA(
    value["json"]["form"]["register_start_date"],
    2,
  );
  value["json"]["form"]["register_end_date"] = convertDateToDisplayA(
    value["json"]["form"]["register_end_date"],
    2,
  );
  value["duracao"] = value["data_c"] + " até " + value["data_t"];
  value["statusString"] = statusX[value["status"]];
  value["cor"] = colorsX[value["status"]];

  if (type === 2) {
    let json = jsonEvent(value.platform);
    if (Number(value.platform) === 2) {
      value.json = normalizeLegacyHomeStreamings(value.json);
      value.json = normalizeHomeRowLimits(value.json);
    }
    value.json = fillMissingProperties(value.json, json);
    value.json.form.fields = value.json.form.fields.map((element) => {
      if (value.json.form.fieldsToShow) {
        let indexToUpdate = value.json.form.fieldsToShow.findIndex(
          (field) => field === element.inputID,
        );
        if (indexToUpdate !== -1) {
          return {
            ...element,
            fieldToShow: true, // Set to true since it is found in fieldsToShow
          };
        } else {
          return {
            ...element,
            fieldToShow: false, // Set to false since it is not found in fieldsToShow
          };
        }
      } else {
        return {
          ...element,
          fieldToShow: false, // Set to false if fieldsToShow is undefined or null
        };
      }
    });
  }
};

export const getEvents = createAsyncThunk(
  "events/axiosGet",
  async ({ selectEventsData, auth }, { signal }) => {
    const isAdmin = Number(auth?.tipo) === 1;
    const requestPlatform = selectEventsData.setID
      ? selectEventsData.selectedEventPlatform ?? selectEventsData.platform
      : selectEventsData.platform;
    const canLoadAllUsers =
      isAdmin && (Boolean(selectEventsData.setID) || selectEventsData.allUsers);
    // userType=2 restricts the list to the authenticated panel user ("Meus eventos").
    // userType=1 loads all users' events ("Eventos de todos os usuários") for admins.
    const effectiveUserType = canLoadAllUsers ? auth.tipo : 2;
    var filterStatus = `status=${encodeURIComponent(
      JSON.stringify(selectEventsData.setStatusEvent),
    )}`;
    var eventsOpened = `recentsEvents=${encodeURIComponent(
      JSON.stringify(selectEventsData.eventsOpened),
    )}`;
    var filterTitle = `titulo=${selectEventsData.setTitleEvent}`;
    var url;

    if (selectEventsData.setID) {
      url = `/api/event/${selectEventsData.setID}?platform=${requestPlatform}&userType=${effectiveUserType}`;
    } else {
      url = `/api/event?${filterStatus}&${filterTitle}&${eventsOpened}&order=${selectEventsData.setDatetimeOrder}&data_criacao=${selectEventsData.setDatetimeEvent}&platform=${selectEventsData.platform}&pagination=${selectEventsData.currentPage}&lastPage=${selectEventsData.lastPage}&limit=${selectEventsData.limit}&userType=${effectiveUserType}`;
    }

    var response = await axios.get(url, { signal });

    return {
      events: response.data.message,
      draftEvents: response.data.draftEvents,
      currentPage: response.data.pagination,
      stopFetching: response.data.stopFetching,
      eventsOpened: selectEventsData.eventsOpened,
      eventGroup01Simple: response.data.eventGroup01Simple
        ? response.data.eventGroup01Simple
        : selectEventsData.eventGroup01Simple,
      eventGroup02Simple: response.data.eventGroup02Simple
        ? response.data.eventGroup02Simple
        : selectEventsData.eventGroup02Simple,
      usersList: response.data.usersList
        ? response.data.usersList
        : selectEventsData.usersList,
    };
  },
);

export const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    setTitleEvent: (state, action) => {
      state.setTitleEvent = action.payload;
      state.setID = initialState.setID;
      state.selectedEventPlatform = initialState.selectedEventPlatform;
      state.showHistory = initialState.showHistory;
      resetPagination(state);
    },
    setDatetimeEvent: (state, action) => {
      state.setDatetimeEvent = action.payload;
      resetPagination(state);
    },
    setStatusEvent: (state, action) => {
      state.setStatusEvent = action.payload;
      resetPagination(state);
    },
    setDatetimeOrder: (state, action) => {
      state.setDatetimeOrder = action.payload;
      resetPagination(state);
    },
    setPlatform: (state, action) => {
      state.platform = action.payload;
      resetPagination(state);
    },
    setAllUsers: (state, action) => {
      state.allUsers = Boolean(action.payload);
      resetPagination(state);
    },
    setEventsOwner: (state, action) => {
      state.ownerUserId = action.payload ?? null;
    },
    resetEventsForUser: (state) => {
      Object.assign(state, initialState);
    },
    resetEventsOpenedFull: (state, action) => {
      state.eventsOpened = initialState.eventsOpened;
      state.eventsOpenedFull = initialState.eventsOpenedFull;
      resetPagination(state);
      state.updateID = state.updateID + 1;
    },
    setID: (state, action) => {
      let arr = state.eventsOpened.filter(
        (obj) =>
          !(
            obj.evento_id === action.payload.id &&
            obj.platform === action.payload.platform
          ),
      );

      // Add the new/updated object to the beginning
      arr.unshift({
        evento_id: action.payload.id,
        platform: action.payload.platform,
      });

      // Keep only the 3 most recent events
      state.eventsOpened = arr.slice(0, 3);
      state.setID = action.payload.id;
      state.selectedEventPlatform = action.payload.platform;
      state.showHistory = action.payload.showHistory || false;
      state.showRecords = action.payload.showRecords || false;
      state.showEmails = action.payload.showEmails || false;
      state.showWhatsApp = action.payload.showWhatsApp || false;
      state.showRsvps = action.payload.showRsvps || false;
      state.showLogs = action.payload.showLogs || false;
      state.value = initialState.value;
      resetPagination(state);
    },
    setEventView: (state, action) => {
      state.showHistory = action.payload.showHistory === true;
      state.showRecords = action.payload.showRecords === true;
      state.showEmails = action.payload.showEmails === true;
      state.showWhatsApp = action.payload.showWhatsApp === true;
      state.showRsvps = action.payload.showRsvps === true;
      state.showLogs = action.payload.showLogs === true;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = state.currentPage + 1;
      state.lastPage = state.lastPage + 1;
      state.pageFetching = true;
    },
    eventList: (state, action) => {
      if (action.payload?.fetch) {
        const preserveCurrent = action.payload?.preserveCurrent === true;
        state.currentPage = initialState.currentPage;
        state.pageFetching = preserveCurrent
          ? true
          : initialState.pageFetching;
        state.firstFetch = initialState.firstFetch;
        state.stopFetching = initialState.stopFetching;
        state.status = preserveCurrent ? initialState.status : "loading";
        state.updateID = state.updateID + 1;
        if (!preserveCurrent && !action.payload?.keepOpen && state.setID > 0) {
          state.setID = initialState.setID;
          state.selectedEventPlatform = initialState.selectedEventPlatform;
          state.showHistory = initialState.showHistory;
          state.showRecords = initialState.showRecords;
          state.showEmails = initialState.showEmails;
          state.showWhatsApp = initialState.showWhatsApp;
          state.showRsvps = initialState.showRsvps;
          state.showLogs = initialState.showLogs;
          state.value = initialState.value;
        }
        if (!preserveCurrent && !action.payload?.keepOpen) {
          state.value = initialState.value;
          state.draftEvents = initialState.draftEvents;
          state.eventsOpenedFull = initialState.eventsOpenedFull;
        }
      } else {
        if (action.payload?.reset) {
          state.value = initialState.value;
          state.eventsOpened = initialState.eventsOpened;
          state.eventsOpenedFull = initialState.eventsOpenedFull;
          state.draftEvents = initialState.draftEvents;
          state.status = initialState.status;
          state.setDatetimeEvent = initialState.setDatetimeEvent;
          state.setDatetimeOrder = initialState.setDatetimeOrder;
          state.setStatusEvent = initialState.setStatusEvent;
          state.platform = initialState.platform;
          state.allUsers = initialState.allUsers;
        }
        state.setID = initialState.setID;
        state.selectedEventPlatform = initialState.selectedEventPlatform;
        state.showHistory = initialState.showHistory;
        state.showRecords = initialState.showRecords;
        state.showEmails = initialState.showEmails;
        state.showWhatsApp = initialState.showWhatsApp;
        state.showRsvps = initialState.showRsvps;
        state.showLogs = initialState.showLogs;
        state.value = initialState.value;
        resetPagination(state);
        state.updateID = state.updateID + 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEvents.pending, (state, action) => {
        if (state.pageFetching === false) {
          state.status = "loading";
        }
        state.currentRequestId = action.meta.requestId;
      })
      .addCase(getEvents.fulfilled, (state, action) => {
        const { requestId } = action.meta;
        if (state.currentRequestId === requestId) {
          let events = action.payload.events;
          let draftEvents = action.payload.draftEvents;
          const currentPage = action.payload.currentPage;
          const stopFetching = action.payload.stopFetching;
          if (Array.isArray(events)) {
            events.forEach(function callback(value, index) {
              dataTreatment(value, index, 1);
            });
            if (state.setID) {
              const current = events.find(
                (e) =>
                  e.evento_id === state.setID &&
                  e.platform === (state.selectedEventPlatform ?? state.platform),
              );
              if (current) state.value = current;
            }
            const openedKeySet = new Set(
              action.payload.eventsOpened.map(
                (e) => `${e.evento_id}_${e.platform}`,
              ),
            );

            // The server prepends recent events before applying the active
            // list filters. Keep them separate on every response so filters
            // never hide or clear the recent-events section.
            const returnedEvents = events.filter(
              (v) =>
                Number(v.status) !== 0 &&
                openedKeySet.has(`${v.evento_id}_${v.platform}`),
            );

            events = events.filter(
              (v) => !openedKeySet.has(`${v.evento_id}_${v.platform}`),
            );

            state.eventsOpenedFull = returnedEvents;

            if (Array.isArray(draftEvents)) {
              draftEvents.forEach(function callback(value, index) {
                dataTreatment(value, index, 1);
              });
              state.draftEvents = draftEvents;
            }
          } else if (events && events.evento_id) {
            // Single-event response (when setID is set)
            dataTreatment(events, 0, 2);
            if (state.setID && events.evento_id === state.setID) {
              state.value = events;
            }
          }
          if (!state.firstFetch && currentPage !== state.lastPage) {
            if (stopFetching === true) {
              state.stopFetching = true;
            } else {
              if (Array.isArray(state.value) && Array.isArray(events)) {
                state.value = [...state.value, ...events];
              } else if (Array.isArray(events)) {
                state.value = [...events];
              } else if (events) {
                // Handle case where events is a single object
                state.value = Array.isArray(state.value)
                  ? [...state.value, events]
                  : [events];
              } else {
                // Fallback if events is null/undefined
                state.value = Array.isArray(state.value) ? state.value : [];
              }
            }
          } else {
            state.firstFetch = false;
            state.value = events;
          }
          state.currentPage = state.lastPage;
          state.pageFetching = false;
          state.status = "idle";
          state.eventGroup01 = action.payload.eventGroup01Simple;
          state.eventGroup02 = action.payload.eventGroup02Simple;
          state.usersList = action.payload.usersList;
          state.currentRequestId = undefined;
        }
      })
      .addCase(getEvents.rejected, (state, action) => {
        const { requestId } = action.meta;
        if (state.currentRequestId === requestId) {
          // A cached event can belong to a different user or no longer exist.
          // Leaving setID populated here makes Events render its detail loader
          // forever because the failed request never supplies value.evento_id.
          if (state.setID) {
            clearSelectedEventState(state);
          }
          state.status = "idle";
          state.pageFetching = false;
          state.currentRequestId = undefined;
        }
      });
  },
});

export const selectEvents = (state) => state.events;

export const {
  setTitleEvent,
  setDatetimeEvent,
  setDatetimeOrder,
  setStatusEvent,
  setPlatform,
  setAllUsers,
  setEventsOwner,
  resetEventsForUser,
  setID,
  setEventView,
  resetEventsOpenedFull,
  setCurrentPage,
  eventList,
} = eventsSlice.actions;

export default eventsSlice.reducer;
